import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";
import { api, errorKey } from "@/lib/api";
import { SaveButton } from "@/pages/admin/settings/CompanyForm";

const PREVIEW_KEYS = ["document_number", "date", "bill_to", "job_site", "description", "hours", "rate", "amount", "total"];

const LangPicker = ({ value, onChange, testId }) => {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-2 gap-2" role="radiogroup">
      {["ES", "EN"].map((l) => (
        <button key={l} type="button" role="radio" aria-checked={value === l} onClick={() => onChange(l)} data-testid={`${testId}-${l}`}
          className={`min-h-[48px] rounded-lg border-2 px-4 text-sm font-bold transition-colors ${
            value === l ? "border-[#000B45] bg-[#000B45] text-white" : "border-[#E5E7EB] bg-white text-[#111827] hover:border-[#000B45]"
          }`}>
          {t(`settings.lang${l}`)}
        </button>
      ))}
    </div>
  );
};

export const DocumentLanguagePanel = () => {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [labels, setLabels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      setSettings(r.data.settings);
      setPreview(r.data.settings.default_document_language);
    }).catch((e) => toast.error(t(errorKey(e))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!preview) return;
    api.get("/documents/labels", { params: { language: preview } }).then((r) => setLabels(r.data)).catch(() => setLabels(null));
  }, [preview]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/settings/company", { ...settings, email: settings.email || null });
      setSettings(r.data.settings);
      toast.success(t("settings.saved"));
    } catch (err) {
      toast.error(t(errorKey(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <form onSubmit={save} className="rounded-xl border border-[#E5E7EB] bg-white p-4 sm:p-6" data-testid="doc-language-form">
        <h2 className="font-display text-lg font-bold text-[#000B45]">{t("settings.docLangTitle")}</h2>
        <p className="mt-1 text-sm text-gray-600">{t("settings.docLangHelp")}</p>
        <p className="mb-2 mt-5 text-sm font-semibold">{t("settings.defaultDocLang")}</p>
        {settings && <LangPicker testId="doc-default-lang" value={settings.default_document_language}
          onChange={(v) => setSettings({ ...settings, default_document_language: v })} />}
        <div className="mt-6 flex justify-end"><SaveButton busy={busy} testId="doc-language-save" /></div>
      </form>
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 sm:p-6" data-testid="doc-label-preview">
        <h2 className="font-display text-lg font-bold text-[#000B45]">{t("settings.previewLang")}</h2>
        <p className="mb-4 mt-1 text-sm text-gray-600">{t("settings.previewHelp")}</p>
        <LangPicker testId="doc-preview-lang" value={preview} onChange={setPreview} />
        {labels && (
          <div className="mt-5 overflow-hidden rounded-lg border border-[#E5E7EB]">
            <div className="flex items-center justify-between bg-[#000B45] px-4 py-3">
              <span data-testid="doc-preview-title" className="font-display text-sm font-extrabold tracking-wider text-white">{labels.labels.titles.invoice}</span>
              <span className="font-mono text-xs text-[#FEB601]">{labels.language}</span>
            </div>
            <dl className="divide-y divide-[#E5E7EB]">
              {PREVIEW_KEYS.map((k) => (
                <dd key={k} data-testid={`doc-preview-${k}`} className="px-4 py-2 text-sm font-semibold text-[#111827]">{labels.labels[k]}</dd>
              ))}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};
